import type { DatabaseConnection } from '@/stores/connectionStore'

interface FunctionParameter {
  name: string
  type: string
  mode?: 'IN' | 'OUT' | 'INOUT'
  position?: number
}

function getDefaultValueForType(dataType: string, dbType: 'mysql' | 'postgresql'): string {
  const type = dataType.toLowerCase()
  
  // Numeric types
  if (type.includes('int') || type.includes('serial') || type.includes('numeric') || 
      type.includes('decimal') || type.includes('float') || type.includes('double') || 
      type.includes('real') || type === 'number') {
    return '1'
  }
  
  // String types
  if (type.includes('varchar') || type.includes('char') || type.includes('text') || 
      type.includes('string') || type === 'name' || type === 'uuid') {
    return dbType === 'mysql' ? "'example_value'" : "'example_value'"
  }
  
  // Date/Time types
  if (type.includes('date') && !type.includes('datetime') && !type.includes('timestamp')) {
    return dbType === 'mysql' ? "'2024-01-01'" : "'2024-01-01'"
  }
  if (type.includes('datetime') || type.includes('timestamp')) {
    return dbType === 'mysql' ? "'2024-01-01 10:00:00'" : "'2024-01-01 10:00:00'"
  }
  if (type.includes('time') && !type.includes('timestamp')) {
    return "'10:00:00'"
  }
  
  // Boolean types
  if (type === 'boolean' || type === 'bool' || type === 'bit') {
    return dbType === 'mysql' ? '1' : 'true'
  }
  
  // JSON types
  if (type.includes('json')) {
    return "'{}'"
  }
  
  // Array types (PostgreSQL)
  if (dbType === 'postgresql' && type.includes('[]')) {
    return "ARRAY[]"
  }
  
  // Default fallback
  return 'NULL'
}

export function parseArgumentsString(
  argumentsString: string | undefined,
  dbType: 'mysql' | 'postgresql'
): FunctionParameter[] {
  if (!argumentsString || argumentsString.trim() === '') {
    return []
  }
  
  const parameters: FunctionParameter[] = []
  let depth = 0
  let currentParam = ''
  let position = 1
  
  // Split by comma but be careful of nested parentheses
  for (const char of argumentsString) {
    if (char === '(') {
      depth++
      currentParam += char
    } else if (char === ')') {
      depth--
      currentParam += char
    } else if (char === ',' && depth === 0) {
      if (currentParam.trim()) {
        const param = parseParameter(currentParam.trim(), position, dbType)
        if (param) parameters.push(param)
        position++
      }
      currentParam = ''
    } else {
      currentParam += char
    }
  }
  
  // Don't forget the last parameter
  if (currentParam.trim()) {
    const param = parseParameter(currentParam.trim(), position, dbType)
    if (param) parameters.push(param)
  }
  
  return parameters
}

function parseParameter(
  paramString: string, 
  position: number, 
  _dbType: 'mysql' | 'postgresql'
): FunctionParameter | null {
  // Handle different parameter formats:
  // MySQL: IN param_name VARCHAR(255), OUT result INT
  // PostgreSQL: param_name text, param2 integer DEFAULT 10
  
  let mode: 'IN' | 'OUT' | 'INOUT' | undefined
  let cleanedParam = paramString
  
  // Extract mode if present (MySQL style)
  if (cleanedParam.toUpperCase().startsWith('IN ')) {
    mode = 'IN'
    cleanedParam = cleanedParam.substring(3).trim()
  } else if (cleanedParam.toUpperCase().startsWith('OUT ')) {
    mode = 'OUT'
    cleanedParam = cleanedParam.substring(4).trim()
  } else if (cleanedParam.toUpperCase().startsWith('INOUT ')) {
    mode = 'INOUT'
    cleanedParam = cleanedParam.substring(6).trim()
  }
  
  // Remove DEFAULT clause if present (PostgreSQL)
  const defaultIndex = cleanedParam.toUpperCase().indexOf(' DEFAULT ')
  if (defaultIndex > -1) {
    cleanedParam = cleanedParam.substring(0, defaultIndex).trim()
  }
  
  // Split into name and type
  const parts = cleanedParam.split(/\s+/)
  if (parts.length === 0) return null
  
  // Handle unnamed parameters (just type)
  if (parts.length === 1) {
    return {
      name: `param${position}`,
      type: parts[0],
      mode: mode || 'IN',
      position
    }
  }
  
  // Named parameters
  const [name] = parts
  const type = parts.slice(1).join(' ')
  
  return {
    name,
    type: type || 'unknown',
    mode: mode || 'IN',
    position
  }
}

export function generateFunctionCall(
  functionName: string,
  argumentsString: string | undefined,
  dbType: 'mysql' | 'postgresql',
  schema?: string,
  database?: string
): string {
  const parameters = parseArgumentsString(argumentsString, dbType)
  const inputParams = parameters.filter(p => p.mode !== 'OUT')
  
  // Generate example values for each parameter
  const exampleValues = inputParams.map(param => 
    getDefaultValueForType(param.type, dbType)
  )
  
  // Build the function call
  let query = ''
  
  if (dbType === 'mysql') {
    const hasOutParams = parameters.some(p => p.mode === 'OUT' || p.mode === 'INOUT')
    
    if (hasOutParams) {
      // MySQL stored procedure with OUT parameters
      const outParamDeclarations = parameters
        .filter(p => p.mode === 'OUT' || p.mode === 'INOUT')
        .map(p => `SET @${p.name} = ${p.mode === 'INOUT' ? getDefaultValueForType(p.type, dbType) : 'NULL'};`)
        .join('\n')
      
      const callParams = parameters.map(p => {
        if (p.mode === 'OUT' || p.mode === 'INOUT') {
          return `@${p.name}`
        }
        return getDefaultValueForType(p.type, dbType)
      }).join(', ')
      
      const selectOutParams = parameters
        .filter(p => p.mode === 'OUT' || p.mode === 'INOUT')
        .map(p => `@${p.name} AS ${p.name}`)
        .join(', ')
      
      query = `-- Declare output variables\n${outParamDeclarations}\n\n`
      query += `-- Call the function\nCALL ${database ? `${database}.` : ''}${functionName}(${callParams});\n\n`
      if (selectOutParams) {
        query += `-- Get the output values\nSELECT ${selectOutParams};`
      }
    } else {
      // Regular MySQL function without OUT parameters
      query = `-- Execute function and get result\nSELECT ${functionName}(${exampleValues.join(', ')}) AS result;`
    }
    
    // Add comment about modifying parameters
    query = `-- Function: ${functionName}\n-- Arguments: ${argumentsString || 'none'}\n-- Modify the parameter values as needed\n\n${query}`
  } else {
    // PostgreSQL function call
    const schemaPrefix = schema && schema !== 'public' ? `${schema}.` : ''
    
    if (parameters.length === 0) {
      query = `-- Execute function without parameters\nSELECT ${schemaPrefix}${functionName}();`
    } else {
      // PostgreSQL supports both positional and named parameters
      const namedParams = inputParams
        .map(p => `${p.name} => ${getDefaultValueForType(p.type, dbType)}`)
        .join(',\n    ')
      
      query = `-- Function: ${functionName}\n` +
              `-- Arguments: ${argumentsString || 'none'}\n` +
              `-- Modify the parameter values as needed\n\n` +
              `-- Using named parameters (recommended):\nSELECT ${schemaPrefix}${functionName}(\n    ${namedParams}\n);\n\n` +
              `-- Or using positional parameters:\n-- SELECT ${schemaPrefix}${functionName}(${exampleValues.join(', ')});`
    }
  }
  
  return query
}

export function generateProcedureCall(
  procedureName: string,
  argumentsString: string | undefined,
  dbType: 'mysql' | 'postgresql',
  schema?: string,
  database?: string
): string {
  const parameters = parseArgumentsString(argumentsString, dbType)
  
  if (dbType === 'mysql') {
    // MySQL CALL syntax
    const exampleValues = parameters.map(param => {
      if (param.mode === 'OUT' || param.mode === 'INOUT') {
        return `@${param.name}`
      }
      return getDefaultValueForType(param.type, dbType)
    })
    
    let query = ''
    
    // Declare OUT variables if needed
    const outParams = parameters.filter(p => p.mode === 'OUT' || p.mode === 'INOUT')
    if (outParams.length > 0) {
      const declarations = outParams.map(p => {
        const initValue = p.mode === 'INOUT' ? getDefaultValueForType(p.type, dbType) : 'NULL'
        return `SET @${p.name} = ${initValue};`
      }).join('\n')
      
      query += `-- Declare output variables\n${declarations}\n\n`
    }
    
    query += `-- Call the procedure\nCALL ${database ? `${database}.` : ''}${procedureName}(${exampleValues.join(', ')});`
    
    // Select OUT variables to see results
    if (outParams.length > 0) {
      const selectVars = outParams.map(p => `@${p.name} AS ${p.name}`).join(', ')
      query += `\n\n-- Get the output values\nSELECT ${selectVars};`
    }
    
    query = `-- Procedure: ${procedureName}\n-- Arguments: ${argumentsString || 'none'}\n-- Modify the parameter values as needed\n\n${query}`
    
    return query
  } else {
    // PostgreSQL CALL syntax (PostgreSQL 11+)
    const schemaPrefix = schema && schema !== 'public' ? `${schema}.` : ''
    
    if (parameters.length === 0) {
      return `-- Execute procedure without parameters\nCALL ${schemaPrefix}${procedureName}();`
    }
    
    // Generate example values
    const exampleValues = parameters.map(param => 
      getDefaultValueForType(param.type, dbType)
    )
    
    const namedParams = parameters
      .map(p => `${p.name} => ${getDefaultValueForType(p.type, dbType)}`)
      .join(',\n    ')
    
    return `-- Procedure: ${procedureName}\n` +
           `-- Arguments: ${argumentsString || 'none'}\n` +
           `-- Modify the parameter values as needed\n\n` +
           `-- Using named parameters (recommended):\nCALL ${schemaPrefix}${procedureName}(\n    ${namedParams}\n);\n\n` +
           `-- Or using positional parameters:\n-- CALL ${schemaPrefix}${procedureName}(${exampleValues.join(', ')});`
  }
}

export function navigateToQueryEditor(
  connection: DatabaseConnection,
  query: string,
  database: string,
  schema?: string,
  newTab = true
): void {
  // Build the URL with query parameters
  const params = new URLSearchParams()
  params.set('query', encodeURIComponent(query))
  params.set('database', database)
  if (schema) {
    params.set('schema', schema)
  }
  if (newTab) {
    params.set('newTab', 'true')
  }
  
  // Navigate to the query editor with the parameters
  const url = `/query/${connection.id}?${params.toString()}`
  window.location.href = url
}